/**
 * Navigation, Mandantenprofile und Rechte — woertlich aus dem Entwurf
 * dem HTML-Prototyp uebernommen (NAV, PROFILE, ROUTE_MODUL, RECHTE).
 *
 * Diese Datei wird nicht „verbessert". Sie ist die Vorlage. Aenderungen an
 * Beschriftung, Reihenfolge oder Zuschnitt gehoeren zuerst in den Entwurf.
 */

export type Modul =
  | 'personen'
  | 'antraege'
  | 'jml'
  | 'rezert'
  | 'sod'
  | 'aenderungsfreigaben'
  | 'personenbezug'
  /**
   * Stuft dieser Mandant Rollen und Objekte nach Kritikalitaet ein?
   *
   * Betrifft ueberall dasselbe Feld: im Rolleneditor, im Rollendetail, als
   * Spalte im Rollenkatalog, als Cockpitkachel — und seit dem 26.08.2026
   * ebenso als Spalte an den Applikationen, den Laufwerken und (unter dem
   * Namen „Vertraulichkeit") an M365.
   *
   * Der Schalter hiess bis dahin `rollenrisiko`. Der Name war zu eng: der
   * Kunde hat am 25.08.2026 den Abschnitt „Verantwortung & Risiko" aus der
   * Rollensicht genommen und am 26.08.2026 dieselbe Spalte an den
   * Applikationen — mit derselben Begruendung, und die gilt fuer jede
   * Objektart: **es gibt keine Einstufung.** Am Bestand nachgezaehlt ist
   * `Kritikalitaet` bei allen 321 Rollen und allen 1.133 Assets leer; die
   * Standardrollenmatrix fuehrt das Feld nicht, `matrix-lesen.py` legt es
   * ausdruecklich leer an. Eine Spalte, in der ueberall ein Strich steht,
   * sieht aus wie eine Auskunft und ist keine.
   *
   * **Die Spalten bleiben** — `iam.Rolle.Kritikalitaet` und
   * `iam.Asset.Kritikalitaet`, Import und Schreibpfad sind unveraendert.
   * Ausgeblendet wird die Anzeige; das laesst sich zuruecknehmen, ein
   * geloeschtes Feld nicht.
   */
  /**
   * Zeigt dieser Mandant die Ausfuehrungsschicht?
   *
   * `iam.Auftrag` steht seit dem 19.08.2026 und wird bei jeder Entscheidung
   * gefuellt, die einen Zugriff aendert. Die **Ansicht** dazu ist eine
   * Betriebsansicht: sie zeigt, was ein Zielsystem noch nicht weiss.
   *
   * HOWOGE hat sie am 27.08.2026 abbestellt — „Runbooks wollen wir erstmal
   * nicht machen und nur die Voraussetzungen geschaffen haben." Solange kein
   * Runbook Auftraege abholt, steht dort eine Liste, die nur waechst, und
   * eine wachsende Liste unerledigter Auftraege liest sich wie ein Rueckstand.
   *
   * **Abgeschaltet ist nur die Ansicht.** Tabelle, Endpunkte und der
   * Auftragsanfall bleiben unveraendert — genau das ist die Voraussetzung,
   * die geschaffen bleiben soll. Wird der erste Runbook-Laeufer angebunden,
   * ist es ein Schalter.
   */
  | 'ausfuehrung'
  /**
   * Fuehrt dieser Mandant den Meldeweg „Anliegen & Fehler" in der Anwendung?
   *
   * HOWOGE nicht: das laeuft dort spaeter ueber ServiceNow (Kunde,
   * 03.09.2026). **Abgeschaltet ist nur die Ansicht.** `iam.Anliegen`, die
   * drei Endpunkte und die drei Pruefer dazu bleiben unveraendert — wer den
   * Weg herausreisst, muss ihn spaeter neu bauen, und die Regel „Melden
   * braucht kein Recht" (18.08.2026) waere zum zweiten Mal zu erarbeiten.
   *
   * Dieselbe Bauart wie `ausfuehrung` und `kritikalitaet`.
   */
  | 'anliegen'
  | 'kritikalitaet'
  /**
   * Fuehrt dieser Mandant einen Gueltigkeitszeitraum je Rolle?
   *
   * Betrifft „Gueltig ab" und „Gueltig bis". Der **Status** haengt
   * ausdruecklich **nicht** daran: er entscheidet, ob eine Rolle als aktiv
   * gilt, und ohne ihn liesse sich keine mehr als Entwurf fuehren.
   */
  | 'rollenfristen'
  /**
   * Fuehrt dieser Mandant die **technische Umsetzung** je Berechtigung?
   *
   * Gemeint sind vier Angaben der Rollenmatrix: der Name der technischen
   * Gruppe, ihre Group ID, der Identitaetsanbieter (`idp`) und der
   * Gruppentyp. Sie beantworten „wie wird dieser Zugriff technisch
   * erteilt" — und sind damit die Voraussetzung dafuer, dass ein Runbook
   * ueberhaupt etwas tun kann.
   *
   * **Am Kundenbestand nachgesehen: alle vier sind leer.**
   * `werkzeuge/matrix-lesen.py` legt `idp`, `gruppentyp` und `groupId`
   * ausdruecklich leer an (Zeilen 406 f.), und die Mappe fuehrt keine
   * Gruppennamen. Fuenf von neun Spalten der Rollenmatrix zeigten damit
   * durchgehend einen Strich.
   *
   * Das ist dieselbe Lage wie bei `kritikalitaet` am 26.08.2026, und die
   * Antwort ist dieselbe: **die Spalten bleiben im Bestand**, die Anzeige
   * wird abgeschaltet. Sobald der Kunde das Gruppenmapping pflegt, ist es
   * ein Schalter — ein geloeschtes Feld waere es nicht.
   *
   * Es haengt daran auch die Route „Technische Gruppen": ohne
   * Gruppenmapping fuehrt der Bestand null Gruppen, und eine leere Liste
   * sieht aus wie ein Ausfall.
   */
  | 'gruppenmapping'
  /**
   * Gleicht dieser Mandant seine technischen Gruppen mit dem Verzeichnis ab?
   *
   * Seit dem 25.09.2026 getrennt von `gruppenmapping`. Die HOWOGE pflegt
   * ihre Gruppen wieder — aber von Hand: Name, Group ID, IdP. Mitgliederzahl
   * und Namenskonvention sind **abgeleitete** Werte, die ein Abgleich mit
   * Entra liefern muesste, und den gibt es dort nicht. `iam.Gruppe.Konform`
   * steht per Vorgabe auf 0; ohne diesen Schalter hiesse jede neu angelegte
   * Gruppe „abweichend" und haette „0 Mitglieder" — eine Pruefung, die nie
   * stattgefunden hat.
   */
  | 'gruppenabgleich';

export interface Eintrag {
  route: string;
  titel: string;
  zeichen: string;
  /**
   * Zahl neben dem Eintrag.
   *
   * Steht seit dem 12.08.2026 ueberall auf `null`. Vorher standen hier
   * dreizehn feste Werte aus der HTML-Vorlage — 309 Rollen, 189
   * Applikationen, 354 Exchange-Eintraege, 51 offene Punkte. Aufgefallen ist
   * es an „Exchange 354", wo eine Handvoll Postfaecher stehen.
   *
   * Die Seitenleiste liest die Zahlen jetzt aus `api/zaehler.ts`, also aus
   * dem Bestand. Das Feld bleibt, weil ein Eintrag kuenftig wieder eine
   * eigene, nicht ableitbare Zahl tragen koennte — belegt wird es nicht
   * mehr.
   */
  anzahl: number | null;
  /** Benoetigtes Recht; null bedeutet: fuer alle Berechtigten sichtbar. */
  recht: string | null;
  beschreibung: string;
  /** Begriffe fuer den Wegweiser und die Suche. */
  begriffe: string;
}

export interface Gruppe {
  titel: string;
  beschreibung: string;
  eintraege: Eintrag[];
}

const e = (
  route: string, titel: string, zeichen: string, anzahl: number | null,
  recht: string | null, beschreibung: string, begriffe: string,
): Eintrag => ({ route, titel, zeichen, anzahl, recht, beschreibung, begriffe });

export const NAVIGATION: Gruppe[] = [
  {
    titel: 'Start',
    beschreibung: 'Der Einstieg — hier beginnen, wenn Sie nicht weiterwissen',
    eintraege: [
      e('/', 'Startseite', 'home', null, null,
        'Alle Aufgaben als Kacheln — der geführte Weg',
        'einstieg start katalog aufgaben launchpad übersicht'),
      e('/cockpit', 'Cockpit', 'grid', null, null,
        'Kennzahlen, Risiken und laufende Vorgänge auf einen Blick',
        'dashboard kennzahlen kpi diagramme auswertung statistik'),
    ],
  },
  {
    titel: 'Rollen & Berechtigungen',
    beschreibung: 'Wer darf was? Aufbau und Inhalt der Standardrollen',
    eintraege: [
      e('/rollen', 'Rollenkatalog', 'liste', null, 'rollen.lesen',
        'Alle Standardrollen — als Tabelle, als Karten oder als Big Picture',
        'rolle stelle suchen liste katalog big picture karten'),
      e('/graph', 'Beziehungsgraph', 'graph', null, 'matrix.lesen',
        'Rolle, Person, Gruppe und Anwendung als Netz dargestellt',
        'netz diagramm beziehung visualisierung verbindung'),
    ],
  },
  {
    titel: 'IT-Assets',
    beschreibung: 'Was es gibt: Anwendungen, Laufwerke, Postfächer, Geräte',
    eintraege: [
      e('/assets/applikationen', 'Applikationen', 'apps', null, 'assets.lesen',
        'Alle Anwendungen mit Owner, Lizenz und Berechtigungskonzept',
        'software anwendung programm sap adobe lizenz app'),
      e('/assets/fileserver', 'Fileserver', 'ordner', null, 'assets.lesen',
        'Laufwerke und Ordner samt Lese- und Schreibrechten',
        'laufwerk ordner verzeichnis pfad share netzlaufwerk'),
      e('/assets/exchange', 'Exchange', 'mail', null, 'assets.lesen',
        'Funktionspostfächer und E-Mail-Verteiler',
        'postfach mail email verteiler mailbox'),
      e('/assets/m365', 'M365 & Teams', 'cloud', null, 'assets.lesen',
        'Teams, M365-Gruppen und SharePoint-Sites',
        'teams sharepoint microsoft 365 cloud gruppe'),
      /* Bis zum 28.08.2026 „Hardware & Mobile". Die mobilen Anwendungen sind
         Anwendungen und stehen seit `052` bei den Applikationen. */
      e('/assets/hardware', 'Hardware', 'laptop', null, 'assets.lesen',
        'Geräte und Standardausstattung',
        'gerät notebook surface iphone ipad handy ausstattung'),
      e('/assets/gruppen', 'Technische Gruppen', 'zahnrad', null, 'assets.lesen',
        'Die Entra- und AD-Gruppen hinter jeder Berechtigung',
        'entra ad gruppe sicherheitsgruppe namenskonvention group id'),
      e('/assets/lizenzen', 'Lizenzen & Kosten', 'euro', null, 'lizenzen.lesen',
        'Was kostet der Rollenbestand? Auswertung und Einsparpotenzial',
        'kosten geld budget preis lizenz euro einsparung'),
    ],
  },
  {
    titel: 'Organisation',
    beschreibung: 'Wer arbeitet wo — Bereiche, Menschen, Vertretungen',
    eintraege: [
      e('/organisation', 'Organisationseinheiten', 'gebaeude', null, 'organisation.lesen',
        /* Ohne Zahl. Hier stand „Die 29 Bereiche" — der Kundenbestand
           fuehrt 30, der Entwicklungsbestand 12. Eine Zahl im
           Beschreibungstext eines Navigationseintrags wird nie
           nachgezaehlt: sie erscheint in Seitenleiste, Wegweiser, Suche,
           `/sitemap` **und im CSV-Export**, und keine dieser Stellen
           fragt den Server. Dieselbe Klasse wie „Exchange 354", das am
           12.08.2026 aus derselben Datei flog. */
        'Bereiche mit ihren Rollen und offenen Punkten',
        'bereich abteilung oe organisation struktur'),
      e('/personen', 'Personen', 'personen', null, 'personen.lesen',
        'Mitarbeitende, ihre Rollen und Vertretungen — mit 360°-Sicht je Person',
        'mitarbeiter mensch person nutzer benutzer wer vertretung beauftragter sbv jav'),
    ],
  },
  {
    titel: 'Anträge & Workflows',
    beschreibung: 'Etwas verändern: beantragen, genehmigen, umsetzen',
    eintraege: [
      e('/antraege/neu', 'Antrag stellen', 'plus', null, null,
        'Zugriff für sich oder andere anfordern',
        'beantragen anfordern neu zugriff wunsch bestellen'),
      e('/antraege/meine', 'Meine Anträge', 'uhr', null, null,
        'Status der eigenen Anträge verfolgen',
        'status verfolgen offen eigene warten'),
      e('/genehmigungen', 'Änderungsfreigaben', 'haken', null, 'genehmigung.entscheiden',
        'Vier-Augen-Prinzip: Änderungen an Rollen, Assets und Gruppen freigeben',
        'freigeben genehmigen ablehnen postkorb entscheiden prüfen vier augen änderung'),
      e('/lifecycle', 'Onboarding / Wechsel / Austritt', 'pfeil', null, 'lifecycle.starten',
        'Neue Mitarbeitende, Stellenwechsel und Austritte abwickeln',
        'eintritt neu joiner mover leaver austritt offboarding onboarding versetzung'),
    ],
  },
  {
    titel: 'Governance',
    beschreibung: 'Nachweisen und kontrollieren, dass alles stimmt',
    eintraege: [
      e('/heatmap', 'Zugriffs-Heatmap', 'heat', null, 'matrix.lesen',
        'Wo häufen sich Zugriffe? Rolle gegen Ressource',
        'wärmekarte häufung risiko intensität vergleich analyse'),
      e('/abfragen', 'Abfragen & Anomalien', 'regel', null, 'governance.lesen',
        'Auffälligkeiten im Bestand aufdecken und eigene Fragen speichern',
        'anomalie regel abfrage query auffälligkeit suche verwaist doppelt konvention'),
      e('/rezertifizierung', 'Rezertifizierung', 'kreis', null, 'governance.verwalten',
        'Bestehende Rechte regelmäßig bestätigen oder entziehen',
        'prüfung kampagne bestätigen entziehen turnus revision'),
      e('/sod', 'SoD-Konflikte', 'warnung', null, 'governance.verwalten',
        'Unvereinbare Rechtekombinationen erkennen und behandeln',
        'funktionstrennung konflikt sod risiko unvereinbar'),
      e('/richtlinien', 'Richtlinien & Namenskonventionen', 'regel', null, 'governance.verwalten',
        'Regeln festlegen: Namensschema, Berechtigungsarten, Genehmiger',
        'regel richtlinie konvention schema katalog vorgabe'),
      e('/offene-punkte', 'Offene Punkte', 'box', null, 'governance.lesen',
        /* Ohne Zahl, aus demselben Grund. „345" war um eine
           Groessenordnung daneben: der Kundenbestand fuehrt 38
           Klaerungspunkte, der Entwicklungsbestand 37. */
        'Klärungsfragen aus der Rollenmatrix — was noch fehlt',
        'todo klärung frage aufgabe offen liste punkt'),
      e('/reports', 'Reports & Exporte', 'export', null, 'reports.lesen',
        /* Kein „als Excel, PDF oder CSV mitnehmen" mehr: einen Export gibt
           es nicht, und ihn im Menue zu versprechen ist dieselbe Klasse wie
           die entfernten Zahlen — nur in Worten statt in Ziffern. */
        'Vorlagen und ein Baukasten über den Bestand',
        'export excel pdf csv bericht auswertung nachweis download'),
    ],
  },
  {
    titel: 'System',
    beschreibung: 'Betrieb, Nachvollziehbarkeit und Rückmeldungen',
    eintraege: [
      /* Die Rollenmatrix steht seit dem 25.08.2026 hier unten.
         Sie zeigt jede einzelne Zeile des Bestands — 18.270 Zuordnungen —
         und ist damit das Werkzeug fuer die Pflege, nicht der Einstieg in
         die taegliche Arbeit. Oben unter „Rollen & Berechtigungen" war sie
         der erste Eintrag und damit das Erste, was ein Neuer anklickte.
         Der Kunde hat sie ausdruecklich nach unten verlangt; das Recht
         `matrix.lesen` bleibt unveraendert. */
      e('/matrix', 'Rollenmatrix', 'matrix', null, 'matrix.lesen',
        'Jede einzelne Berechtigung — von der Anwendung bis zur technischen Gruppe',
        'tabelle excel berechtigung zuordnung gruppe group id entra ad'),
      e('/rechte', 'Berechtigungsfeld', 'schild', null, 'rechte.lesen',
        'Wer darf was — Rechte je Anwendungsrolle anklicken',
        'recht rolle rbac berechtigung vergabe matrix anwendungsrolle admin'),
      e('/konten', 'Konten & Rollen', 'personen', null, 'konten.lesen',
        'Wer sich angemeldet hat, welche Anwendungsrolle er trägt — und wer noch auf eine wartet',
        'konto benutzer anmeldung rolle zuweisen freigabe warteschlange'),
      e('/aktive-nutzer', 'Aktive Nutzer', 'blitz', null, 'admin.nutzung',
        'Wer ist gerade in der Anwendung, wer hat sich angemeldet',
        'online präsenz nutzung anmeldung session'),
      /* Die dritte Ebene: Soll (Rolle), Ist (`iam.Zugriff`) und was ein
         Zielsystem noch nicht weiss. Sie stand seit dem 19.08.2026 als
         offener Punkt — die Tabelle gab es, die Ansicht
         nicht. */
      e('/ausfuehrung', 'Ausführung', 'blitz', null, 'auftrag.lesen',
        'Offene, laufende und fehlgeschlagene Aufträge an die Zielsysteme',
        'auftrag runbook entra provisionierung ausfuehrung zielsystem'),
      e('/audit', 'Audit-Log', 'verlauf', null, 'admin.audit',
        'Jede Änderung mit Vorher-Nachher-Vergleich',
        'protokoll änderung historie log revisionssicher'),
      e('/sitemap', 'Site-Map & Architektur', 'sitemap', null, 'admin.sitemap',
        'Alle Seiten, das Datenmodell und die technische Architektur',
        'struktur architektur datenmodell doku seitenübersicht'),
      /* **Ohne Recht**, und das ist die ganze Aussage dieser Zeile.
         Bis zum 18.08.2026 stand hier `dev.issues`. Gemessen an diesem Tag:
         ein Konto ohne dieses Recht bekam auf `/dev/issues` „Keine
         Berechtigung" — die Seite mit dem Meldeformular war ausgerechnet für
         die gesperrt, die etwas zu melden haben. Wer auf eine Wand läuft,
         ist der häufigste Anlass für eine Meldung und regelmäßig jemand mit
         zu wenig Rechten.

         Die Route steht deshalb jedem offen, der ein Konto hat. Was jemand
         **sieht**, entscheidet die Seite: eigene Meldungen immer, alle nur
         mit `anliegen.lesen.alle` (`030`). `dev.issues` bleibt im Katalog —
         es beschreibt „Entwicklungsstand einsehen" und wird anderswo
         gebraucht. */
      e('/dev/issues', 'Anliegen & Fehler', 'tastatur', null, null,
        'Fehler melden, Wünsche einreichen und den Stand der eigenen Meldungen sehen',
        'fehler bug wunsch idee melden feedback issue anliegen'),
    ],
  },
];

/* ----------------------------------------------------------- Mandanten */

export interface Profil {
  name: string;
  primaer: string;
  module: Record<Modul, boolean>;
}

export const PROFILE: Record<string, Profil> = {
  comline: {
    name: 'COMLINE',
    primaer: '#D84F22',
    module: {
      personen: true, antraege: true, jml: true, rezert: true, sod: true,
      aenderungsfreigaben: true, personenbezug: true,
      kritikalitaet: true, rollenfristen: true, ausfuehrung: true, anliegen: true,
      gruppenmapping: true, gruppenabgleich: true,
    },
  },
  /*
   * Der Mandant des Wohnungsunternehmens. Er stand hier bis zum 11.08.2026,
   * war dann acht Tage lang entfallen und kommt am 19.08.2026 zurück —
   * mit einer Bedingung, die vorher fehlte: **er lebt in einer eigenen
   * Instanz.**
   *
   * Der Grund für das Entfernen war nicht das Profil, sondern der Bestand.
   * Der Import schrieb seine Zeilen in **jeden** Mandanten; der
   * COMLINE-Mandant wurde dadurch zur Kopie fremder Kundendaten. Das kann
   * nicht wiederkehren: die zweite Instanz hat eine eigene Datenbank, und
   * ihre API kennt über `MANDANTEN` nur diesen einen Code.
   *
   * Der Zuschnitt ist derselbe, den bis dahin nur der Demobau führte
   * (`apps/web/demo/einrichtung.ts`): kein Personenbezug, kein Antragswesen,
   * kein Lebenszyklus, keine Rezertifizierung, keine Funktionstrennung.
   * Änderungsfreigaben bleiben an — das Vier-Augen-Prinzip über Rollen und
   * Assets ist kein Antragsweg und wird gebraucht.
   *
   * Die Farbtokens stehen in `stile/tokens.css` (`data-marke="howoge"`) und
   * werden in beiden Themes auf Kontrast gemessen.
   */
  howoge: {
    name: 'HOWOGE',
    primaer: '#009EE3',
    module: {
      personen: false, antraege: false, jml: false, rezert: false, sod: false,
      aenderungsfreigaben: true, personenbezug: false,
      /* Am 25.08.2026 dazugekommen. Der Kunde fuehrt weder eine
         Risikoeinstufung noch einen Gueltigkeitszeitraum je Rolle; die
         beiden Abschnitte des Rolleneditors entfallen damit ganz. Die
         Spalten bleiben im Bestand. */
      kritikalitaet: false, rollenfristen: false,
      /* Am 27.08.2026 abbestellt, siehe den Modulkommentar oben. */
      ausfuehrung: false, anliegen: false,
      /* Vom 27.08. bis 25.09.2026 aus: Gruppenname, Group ID, IDP und
         Gruppentyp waren im ganzen Bestand leer, und fuenf von neun
         Spalten der Rollenmatrix zeigten einen Strich. */
      gruppenmapping: true,
      /* Am 25.09.2026 wieder eingeschaltet: der Kunde pflegt die Gruppen
         jetzt als eigenen Bestand (`062`, Regel je Objekt und Zugriffsart).
         Was noch fehlt, heisst „noch nicht hinterlegt" — ausdruecklich und
         ohne Vorschlag. Der Abgleich mit dem Verzeichnis bleibt aus. */
      gruppenabgleich: false,
    },
  },
  /*
   * Entwicklungsmandant. Traegt ausschliesslich Wegwerf-Testdaten und ist
   * gegen die Bestandsmandanten getrennt: eigene MandantId in der Datenbank,
   * eigener Endpunkt im Entwicklungsbetrieb, keine gemeinsamen Zeilen.
   *
   * Voller Funktionsumfang, damit sich jede Ansicht pruefen laesst — auch
   * die, die HOWOGE nicht kennt.
   */
  dev: {
    name: 'DEV',
    primaer: '#5B8C6E',
    module: {
      personen: true, antraege: true, jml: true, rezert: true, sod: true,
      aenderungsfreigaben: true, personenbezug: true,
      kritikalitaet: true, rollenfristen: true, ausfuehrung: true, anliegen: true,
      gruppenmapping: true, gruppenabgleich: true,
    },
  },
};

/** Mandanten, die niemals Bestandsdaten fuehren duerfen. */
export const NUR_TESTDATEN = new Set(['dev']);

/**
 * Welche Route welchen Modulschalter braucht. Die Pruefung greift **vor** der
 * Ansichtsweiche — abgeschaltete Module sind auch ueber die Adresszeile nicht
 * erreichbar.
 */
export const ROUTE_MODUL: Record<string, Modul> = {
  '/personen': 'personen',
  '/vertretungen': 'personen',
  '/antraege': 'antraege',
  /* Aenderungsfreigaben hingen bis zum 18.08.2026 am Antragswesen — wer sie
     zeigte, zeigte auch „Antrag stellen" und „Meine Antraege". Sie sind aber
     das Vier-Augen-Prinzip ueber Rollen, Assets und Gruppen, kein Antragsweg.
     Deshalb haengen sie am eigenen, bis dahin ungenutzten Modul. Fuer Mandanten
     mit beiden Modulen aendert sich nichts. */
  '/genehmigungen': 'aenderungsfreigaben',
  '/postkorb': 'antraege',
  '/lifecycle': 'jml',
  '/rezertifizierung': 'rezert',
  '/sod': 'sod',
  /* Technische Gruppen haengen am Gruppenmapping — sie **sind** es.

     Ohne gepflegtes Mapping fuehrt der Bestand null Gruppen: die
     Standardrollenmatrix nennt keine Gruppennamen, und der Wandler legt
     `groupId` und `gruppentyp` leer an. Eine Liste, die immer leer ist,
     sieht aus wie ein Ausfall der Schnittstelle und nicht wie eine
     Eigenschaft der Quelle. */
  '/assets/gruppen': 'gruppenmapping',
  /* Drei Bereiche hingen an keinem Modul, weil der Nachbau sie bisher nur
     über Rechte begrenzte. Der vereinbarte Zuschnitt des Wohnungsunternehmens
     führt sie nicht — sie hängen deshalb an Modulen, die dessen Profil
     abschaltet. Für Mandanten mit vollem Umfang ändert sich nichts: COMLINE
     und DEV haben beide Schalter an.

     Bis zum 19.08.2026 stand diese Zuordnung nur im Demobau. Ein Zuschnitt,
     der allein in einem Nebenbau lebt, gilt für die ausgelieferte Anwendung
     nicht — und genau die bekommt der Kunde jetzt. */
  '/organisation': 'personen',
  '/ausfuehrung': 'ausfuehrung',
  '/dev/issues': 'anliegen',
  '/abfragen': 'sod',
  '/richtlinien': 'sod',
};

/* ------------------------------------------------------------- Rechte */

/* `RECHTE` ist am 27.08.2026 entfallen — mit ihr `darf(...)` weiter unten.

   Die Tabelle nannte je Persona die Rechte, die diese Persona angeblich
   traegt. Sie speiste die Auskunft auf `/profil` („Die Anwendungsrolle
   *IAM Admin* traegt **alle Rechte**") und die Sperren im Wegweiser und in
   der Suchpalette. Ihr Schluessel war die Persona — und die stand im
   **Browserspeicher**: wer sie umstellte, aenderte damit die Auskunft ueber
   seine eigenen Rechte. Fuenf der Personas gibt es im Bestand seit dem
   Rollenumbau vom 18.08.2026 ohnehin nicht mehr.

   Es gilt jetzt ueberall dasselbe wie in `App.tsx` seit dem 12.08.2026:
   die Rechte kommen aus `/api/ich`, und `hatRecht(...)` prueft gegen sie.
   Solange sie nicht bekannt sind, lautet die Antwort **nein**.

   Die Personas des Demobaus stehen als `DEMO_PERSONAS` in
   `entwurf/demo.ts`; dort wechseln sie das Konto, nicht die Rechte. */

/**
 * Das Recht, das eine Route verlangt — laengster passender Praefix.
 *
 * Dieselbe Ueberlegung wie bei `modulFuerRoute`: `/personen/P-008` verlangt
 * dasselbe Recht wie `/personen`. Ohne Praefixtreffer waere jede Detailroute
 * ungeschuetzt.
 *
 * Die Quelle ist die Navigation selbst. Damit gibt es genau eine Stelle, an
 * der steht, welches Recht wozu gehoert — ein zweiter Katalog waere ein
 * zweiter Ort zum Vergessen.
 *
 * Rueckgabe `null` heisst: kein Recht noetig (Startseite, Cockpit, Profil).
 */
export function rechtFuerRoute(route: string): string | null {
  const pfad = route.split(/[?#]/)[0] ?? '';
  let treffer: string | null = null;
  let laenge = -1;
  for (const gruppe of NAVIGATION) {
    for (const eintrag of gruppe.eintraege) {
      const passt = pfad === eintrag.route
        || (eintrag.route !== '/' && pfad.startsWith(eintrag.route + '/'));
      if (passt && eintrag.route.length > laenge) {
        treffer = eintrag.recht;
        laenge = eintrag.route.length;
      }
    }
  }
  return treffer;
}

/**
 * Prueft ein Recht gegen die Liste, die die Schnittstelle liefert.
 *
 * Die einzige Rechtepruefung der Oberflaeche. Ihre Quelle ist `/api/ich`,
 * also `iam.Kontorolle` und `iam.Rollenrecht`; vom Browser laesst sie sich
 * nicht aendern.
 *
 * Daneben stand bis zum 27.08.2026 `darf(rollen, recht)`, das aus einer
 * **Persona** ableitete — einer Angabe des Browsers. Vier Stellen benutzten
 * noch sie: Wegweiser, Suchpalette, Startkacheln und die Zaehler. Sie sind
 * auf die echten Rechte umgestellt.
 *
 * `recht === null` heisst „kein Recht noetig" (Startseite, Cockpit, Profil)
 * und ist deshalb erlaubt.
 */
export function hatRecht(rechte: readonly string[], recht: string | null): boolean {
  if (!recht) return true;
  return rechte.includes(recht);
}

/** Ist die Route im gewaehlten Mandanten ueberhaupt erreichbar? */
/**
 * Das Modul, unter dem eine Route steht — laengster passender Praefix.
 *
 * Ein exakter Vergleich reicht nicht: `/personen/P-008?tab=zugriff` gehoert
 * genauso zum Modul `personen` wie `/personen`. Ohne Praefixtreffer waere jede
 * Detailroute ungeschuetzt, und ein Mandant ohne Personenbezug bekaeme
 * Personendaten zu sehen, sobald irgendwo ein Link darauf zeigt.
 */
export function modulFuerRoute(route: string): Modul | undefined {
  const pfad = route.split(/[?#]/)[0] ?? '';
  let treffer: Modul | undefined;
  let laenge = -1;
  for (const [praefix, modul] of Object.entries(ROUTE_MODUL)) {
    const passt = pfad === praefix || pfad.startsWith(praefix + '/');
    if (passt && praefix.length > laenge) {
      treffer = modul;
      laenge = praefix.length;
    }
  }
  return treffer;
}

export function routeErlaubt(route: string, profil: Profil): boolean {
  const modul = modulFuerRoute(route);
  return modul === undefined || profil.module[modul];
}

/**
 * Fuehrt ein **Ziel** in ein Modul, das diese Instanz fuehrt?
 *
 * Wie `routeErlaubt`, nur fuer die Ziele aus Katalog und „Neu"-Menue: die
 * tragen einen Suchteil (`?neu=1`) oder sind gar keine Route. Ein
 * Drawer-Formular (`form:`) haengt an keinem Modul und gilt als
 * eingeschaltet — dort zieht das Recht die Grenze.
 */
export function zielErlaubt(ziel: string, profil: Profil): boolean {
  if (ziel.startsWith('form:') || ziel === 'wegweiser') return true;
  return routeErlaubt(ziel.split('?')[0] ?? ziel, profil);
}
